import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { SERVER_URL } from '../config';
import { Bars3Icon } from "@heroicons/react/24/outline";
import Sidebar from '../components/Sidebar';


const CATEGORIES = ['Quantitative', 'Qualitative', 'Learning', 'Cross Functional', 'Behavioral'];
const CYCLES = ['monthly', 'quarterly', 'annual'];

const now = new Date();
const defaultMonthly = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
const defaultQuarter = `${now.getFullYear()}-Q${Math.ceil((now.getMonth() + 1) / 3)}`;

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

const LABEL_STYLES = {
    'Poor': 'bg-rose-50 text-rose-700 border-rose-200',
    'Moderate': 'bg-amber-50 text-amber-700 border-amber-200',
    'Average': 'bg-amber-50 text-amber-700 border-amber-200',
    'Fair': 'bg-amber-50 text-amber-700 border-amber-200',
    'Above Average': 'bg-sky-50 text-sky-700 border-sky-200',
    'Decent': 'bg-sky-50 text-sky-700 border-sky-200',
    'Good': 'bg-emerald-50 text-emerald-700 border-emerald-200',
    'Excellent': 'bg-emerald-100 text-emerald-800 border-emerald-300',
    'Pending': 'bg-slate-50 text-slate-500 border-slate-200',
    'Not Rated': 'bg-slate-50 text-slate-500 border-slate-200',
};

function RatingBadge({ label }) {
    const cls = LABEL_STYLES[label] || 'bg-slate-50 text-slate-600 border-slate-200';
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cls}`}>
            {label}
        </span>
    );
}

function scoreColor(pct) {
    if (pct >= 91) return 'text-emerald-600';
    if (pct >= 81) return 'text-sky-600';
    if (pct >= 61) return 'text-amber-600';
    return 'text-rose-600';
}

function ErrorBanner({ error, onDismiss }) {
    if (!error) return null;
    return (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg px-4 py-2 flex justify-between">
            <span>{error}</span>
            <button onClick={onDismiss} className="font-medium">Dismiss</button>
        </div>
    );
}

function extractError(err) {
    return err?.response?.data?.error || err?.message || 'Something went wrong';
}

const emptyParam = (template_id) => ({
    template_id,
    category: 'Quantitative',
    parameter_name: '',
    measurement_criteria: '',
    default_target_value: '',
    target_unit: '',
    weightage: '',
    score_type: 'auto',
    cycle: 'monthly',
    sort_order: 0,
    bands: [
        { min_pct: 0, max_pct: 60.99, label: 'Poor' },
        { min_pct: 61, max_pct: 80.99, label: 'Average' },
        { min_pct: 81, max_pct: 90.99, label: 'Above Average' },
        { min_pct: 91, max_pct: 100.99, label: 'Good' },
        { min_pct: 101, max_pct: null, label: 'Excellent' },
    ],
});

// ---------------------------------------------------------------------------
// Admin: Template Builder
// ---------------------------------------------------------------------------

function TemplateBuilder() {
    const [templates, setTemplates] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [template, setTemplate] = useState(null);
    const [editingParam, setEditingParam] = useState(null);
    const [newTemplate, setNewTemplate] = useState({ template_name: '', job_role: '', job_position: '', description: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const loadTemplates = () =>
        axios.get(`${SERVER_URL}/Rahul/templates.php`, { params: { action: 'list' } })
            .then((res) => setTemplates(res.data.data))
            .catch((e) => setError(extractError(e)));

    useEffect(() => { loadTemplates(); }, []);

    useEffect(() => {
        if (!selectedId) { setTemplate(null); return; }
        setLoading(true);
        axios.get(`${SERVER_URL}/Rahul/templates.php`, { params: { action: 'detail', template_id: selectedId } })
            .then((res) => setTemplate(res.data.data))
            .catch((e) => setError(extractError(e)))
            .finally(() => setLoading(false));
    }, [selectedId]);

    async function handleCreateTemplate(e) {
        e.preventDefault();
        if (!newTemplate.template_name || !newTemplate.job_role) return;
        try {
            const res = await axios.post(`${SERVER_URL}/Rahul/templates.php`, { action: 'create_template', ...newTemplate });
            setNewTemplate({ template_name: '', job_role: '', job_position: '', description: '' });
            await loadTemplates();
            setSelectedId(res.data.data.template_id);
        } catch (e) { setError(extractError(e)); }
    }

    async function handleSaveParam(param) {
        try {
            await axios.post(`${SERVER_URL}/Rahul/templates.php`, { action: 'save_parameter', ...param });
            setEditingParam(null);
            const res = await axios.get(`${SERVER_URL}/Rahul/templates.php`, { params: { action: 'detail', template_id: selectedId } });
            setTemplate(res.data.data);
        } catch (e) { setError(extractError(e)); }
    }

    async function handleDeleteParam(param_id) {
        if (!window.confirm('Remove this parameter from the template?')) return;
        try {
            await axios.post(`${SERVER_URL}/Rahul/templates.php`, { action: 'delete_parameter', param_id });
            const res = await axios.get(`${SERVER_URL}/Rahul/templates.php`, { params: { action: 'detail', template_id: selectedId } });
            setTemplate(res.data.data);
        } catch (e) { setError(extractError(e)); }
    }

    const categoryWeightTotal = (cat) =>
        (template?.parameters || [])
            .filter((p) => p.category === cat)
            .reduce((sum, p) => sum + Number(p.weightage), 0);

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold text-slate-800">KRA Template Builder</h1>
                <p className="text-sm text-slate-500 mt-1">Define parameters, weightages, targets, and rating bands per job role.</p>
            </div>

            <ErrorBanner error={error} onDismiss={() => setError(null)} />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-1 space-y-4">
                    <div className="bg-white border border-slate-200 rounded-xl p-4">
                        <h2 className="text-sm font-semibold text-slate-700 mb-3">Templates</h2>
                        <div className="space-y-1 max-h-72 overflow-y-auto">
                            {templates.map((t) => (
                                <button
                                    key={t.template_id}
                                    onClick={() => setSelectedId(t.template_id)}
                                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition ${selectedId === t.template_id ? 'bg-indigo-50 text-indigo-700 font-medium' : 'hover:bg-slate-50 text-slate-600'
                                        }`}
                                >
                                    <div>{t.template_name}</div>
                                    <div className="text-xs text-slate-400">{t.job_role}{t.job_position ? ` · ${t.job_position}` : ''}</div>
                                </button>
                            ))}
                            {templates.length === 0 && <p className="text-sm text-slate-400">No templates yet.</p>}
                        </div>
                    </div>

                    <form onSubmit={handleCreateTemplate} className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
                        <h2 className="text-sm font-semibold text-slate-700">New Template</h2>
                        <input className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm" placeholder="Template name"
                            value={newTemplate.template_name} onChange={(e) => setNewTemplate({ ...newTemplate, template_name: e.target.value })} />
                        <input className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm" placeholder="Job role (matches employee_tb.job_role)"
                            value={newTemplate.job_role} onChange={(e) => setNewTemplate({ ...newTemplate, job_role: e.target.value })} />
                        <input className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm" placeholder="Job position (optional)"
                            value={newTemplate.job_position} onChange={(e) => setNewTemplate({ ...newTemplate, job_position: e.target.value })} />
                        <textarea className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm" placeholder="Description" rows={2}
                            value={newTemplate.description} onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })} />
                        <button className="w-full bg-indigo-600 text-white text-sm rounded-lg py-1.5 hover:bg-indigo-700">Create Template</button>
                    </form>
                </div>

                <div className="md:col-span-2">
                    {loading && <p className="text-sm text-slate-400">Loading…</p>}
                    {!loading && template && (
                        <div className="space-y-5">
                            <div className="bg-white border border-slate-200 rounded-xl p-4">
                                <h2 className="text-lg font-semibold text-slate-800">{template.template_name}</h2>
                                <p className="text-xs text-slate-400">{template.job_role}{template.job_position ? ` · ${template.job_position}` : ''}</p>
                            </div>

                            {CATEGORIES.map((cat) => (
                                <div key={cat} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                                    <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                                        <h3 className="text-sm font-semibold text-slate-700">{cat}</h3>
                                        <div className="flex items-center gap-3">
                                            <span className={`text-xs ${categoryWeightTotal(cat) === 100 ? 'text-emerald-600' : 'text-amber-600'}`}>
                                                Total weight: {categoryWeightTotal(cat)}{categoryWeightTotal(cat) !== 100 && ' (target 100)'}
                                            </span>
                                            <button
                                                onClick={() => setEditingParam(emptyParam(template.template_id))}
                                                className="text-xs text-indigo-600 font-medium hover:underline"
                                            >+ Add parameter</button>
                                        </div>
                                    </div>
                                    <div className="divide-y divide-slate-100">
                                        {template.parameters.filter((p) => p.category === cat).map((p) => (
                                            <div key={p.param_id} className="px-4 py-3 flex items-start justify-between gap-3">
                                                <div className="min-w-0">
                                                    <p className="text-sm text-slate-800">{p.parameter_name}</p>
                                                    <p className="text-xs text-slate-400 mt-0.5">{p.measurement_criteria}</p>
                                                    <div className="flex gap-2 mt-1.5">
                                                        <span className="text-xs bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{p.cycle}</span>
                                                        <span className="text-xs bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{p.score_type}</span>
                                                        <span className="text-xs bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">weight {p.weightage}</span>
                                                    </div>
                                                </div>
                                                <div className="flex gap-2 shrink-0">
                                                    <button onClick={() => setEditingParam({ ...p, bands: p.bands?.length ? p.bands : emptyParam(template.template_id).bands })}
                                                        className="text-xs text-slate-500 hover:text-indigo-600">Edit</button>
                                                    <button onClick={() => handleDeleteParam(p.param_id)} className="text-xs text-slate-500 hover:text-rose-600">Remove</button>
                                                </div>
                                            </div>
                                        ))}
                                        {template.parameters.filter((p) => p.category === cat).length === 0 && (
                                            <p className="px-4 py-3 text-sm text-slate-400">No parameters in this category yet.</p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {!loading && !template && <p className="text-sm text-slate-400">Select or create a template to start building.</p>}
                </div>
            </div>

            {editingParam && (
                <ParamEditorModal
                    param={editingParam}
                    onClose={() => setEditingParam(null)}
                    onSave={handleSaveParam}
                />
            )}
        </div>
    );
}

function ParamEditorModal({ param, onClose, onSave }) {
    const [form, setForm] = useState(param);

    const updateBand = (idx, field, value) => {
        const bands = [...form.bands];
        bands[idx] = { ...bands[idx], [field]: value };
        setForm({ ...form, bands });
    };
    const addBand = () => setForm({ ...form, bands: [...form.bands, { min_pct: 0, max_pct: null, label: '' }] });
    const removeBand = (idx) => setForm({ ...form, bands: form.bands.filter((_, i) => i !== idx) });

    return (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-5 space-y-3">
                <h3 className="text-base font-semibold text-slate-800">{form.param_id ? 'Edit' : 'Add'} Parameter</h3>

                <label className="block text-xs text-slate-500">Category
                    <select className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm mt-1"
                        value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                        {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                </label>

                <label className="block text-xs text-slate-500">Parameter name
                    <textarea className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm mt-1" rows={2}
                        value={form.parameter_name} onChange={(e) => setForm({ ...form, parameter_name: e.target.value })} />
                </label>

                <label className="block text-xs text-slate-500">Measurement criteria
                    <input className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm mt-1"
                        value={form.measurement_criteria || ''} onChange={(e) => setForm({ ...form, measurement_criteria: e.target.value })} />
                </label>

                <div className="grid grid-cols-2 gap-3">
                    <label className="block text-xs text-slate-500">Default target
                        <input type="number" className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm mt-1"
                            value={form.default_target_value || ''} onChange={(e) => setForm({ ...form, default_target_value: e.target.value })} />
                    </label>
                    <label className="block text-xs text-slate-500">Unit
                        <input className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm mt-1"
                            value={form.target_unit || ''} onChange={(e) => setForm({ ...form, target_unit: e.target.value })} />
                    </label>
                </div>

                <div className="grid grid-cols-3 gap-3">
                    <label className="block text-xs text-slate-500">Weightage
                        <input type="number" className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm mt-1"
                            value={form.weightage} onChange={(e) => setForm({ ...form, weightage: e.target.value })} />
                    </label>
                    <label className="block text-xs text-slate-500">Score type
                        <select className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm mt-1"
                            value={form.score_type} onChange={(e) => setForm({ ...form, score_type: e.target.value })}>
                            <option value="auto">Auto (target vs achieved)</option>
                            <option value="manual">Manual (manager score)</option>
                        </select>
                    </label>
                    <label className="block text-xs text-slate-500">Cycle
                        <select className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm mt-1"
                            value={form.cycle} onChange={(e) => setForm({ ...form, cycle: e.target.value })}>
                            {CYCLES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </label>
                </div>

                <div>
                    <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-slate-500">Rating bands (achievement % → label)</span>
                        <button onClick={addBand} className="text-xs text-indigo-600 hover:underline">+ Add band</button>
                    </div>
                    <div className="space-y-1.5">
                        {form.bands.map((b, idx) => (
                            <div key={idx} className="flex items-center gap-1.5">
                                <input type="number" placeholder="min %" className="w-16 border border-slate-300 rounded px-2 py-1 text-xs"
                                    value={b.min_pct} onChange={(e) => updateBand(idx, 'min_pct', e.target.value)} />
                                <span className="text-xs text-slate-400">–</span>
                                <input type="number" placeholder="max % (blank=∞)" className="w-24 border border-slate-300 rounded px-2 py-1 text-xs"
                                    value={b.max_pct ?? ''} onChange={(e) => updateBand(idx, 'max_pct', e.target.value === '' ? null : e.target.value)} />
                                <input placeholder="label" className="flex-1 border border-slate-300 rounded px-2 py-1 text-xs"
                                    value={b.label} onChange={(e) => updateBand(idx, 'label', e.target.value)} />
                                <button onClick={() => removeBand(idx)} className="text-xs text-rose-500">✕</button>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                    <button onClick={onClose} className="text-sm px-3 py-1.5 rounded-lg text-slate-600 hover:bg-slate-100">Cancel</button>
                    <button onClick={() => onSave(form)} className="text-sm px-3 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700">Save</button>
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Manager: Score Entry
// ---------------------------------------------------------------------------

function ScoreEntry({ enteredBy = 'system' }) {
    const [employeeId, setEmployeeId] = useState('');
    const [periodType, setPeriodType] = useState('monthly');
    const [periodValue, setPeriodValue] = useState(defaultMonthly);
    const [data, setData] = useState(null);
    const [form, setForm] = useState({});
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        setPeriodValue(periodType === 'monthly' ? defaultMonthly : periodType === 'quarterly' ? defaultQuarter : String(now.getFullYear()));
    }, [periodType]);

    async function loadEntry() {
        if (!employeeId || !periodValue) return;
        setLoading(true);
        setError(null);
        setResult(null);
        try {
            const res = await axios.get(`${SERVER_URL}/Rahul/achievements.php`, {
                params: { action: 'for_period', employee_id: employeeId, period_type: periodType, period_value: periodValue },
            });
            const d = res.data.data;
            setData(d);
            const initial = {};
            d.parameters.forEach((p) => {
                initial[p.param_id] = {
                    target_value: p.existing?.target_value ?? p.default_target_value ?? '',
                    achieved_value: p.existing?.achieved_value ?? '',
                    manual_score_pct: p.existing?.manual_score_pct ?? '',
                    remarks: p.existing?.remarks ?? '',
                };
            });
            setForm(initial);
        } catch (e) {
            setError(extractError(e));
            setData(null);
        } finally {
            setLoading(false);
        }
    }

    function updateField(param_id, field, value) {
        setForm({ ...form, [param_id]: { ...form[param_id], [field]: value } });
    }

    async function handleSubmit() {
        const entries = data.parameters.map((p) => ({
            employee_id: employeeId,
            param_id: p.param_id,
            period_type: periodType,
            period_value: periodValue,
            entered_by: enteredBy,
            ...form[p.param_id],
        }));
        try {
            const res = await axios.post(`${SERVER_URL}/Rahul/achievements.php`, { action: 'save_bulk', entries });
            setResult(res.data.data[0]);
        } catch (e) {
            setError(extractError(e));
        }
    }

    return (
        <div className="space-y-5">
            <div>
                <h1 className="text-2xl font-semibold text-slate-800">KRA Score Entry</h1>
                <p className="text-sm text-slate-500 mt-1">Enter achievements for a period. Quantitative parameters take target/achieved values; the rest take a manager-assessed score.</p>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-end gap-3">
                <label className="text-xs text-slate-500">Employee ID
                    <input className="block border border-slate-300 rounded-lg px-3 py-1.5 text-sm mt-1 w-40"
                        value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} placeholder="e.g. EMP001" />
                </label>
                <label className="text-xs text-slate-500">Period type
                    <select className="block border border-slate-300 rounded-lg px-3 py-1.5 text-sm mt-1"
                        value={periodType} onChange={(e) => setPeriodType(e.target.value)}>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="annual">Annual</option>
                    </select>
                </label>
                <label className="text-xs text-slate-500">Period value
                    <input className="block border border-slate-300 rounded-lg px-3 py-1.5 text-sm mt-1 w-32"
                        value={periodValue} onChange={(e) => setPeriodValue(e.target.value)} />
                </label>
                <button onClick={loadEntry} className="bg-slate-800 text-white text-sm rounded-lg px-4 py-1.5 hover:bg-slate-900">Load</button>
            </div>

            <ErrorBanner error={error} onDismiss={() => setError(null)} />
            {loading && <p className="text-sm text-slate-400">Loading…</p>}

            {data && (
                <div className="space-y-4">
                    {['Quantitative', 'Qualitative', 'Learning', 'Cross Functional', 'Behavioral']
                        .filter((cat) => data.parameters.some((p) => p.category === cat))
                        .map((cat) => (
                            <div key={cat} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                                <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                                    <h3 className="text-sm font-semibold text-slate-700">{cat}</h3>
                                </div>
                                <div className="divide-y divide-slate-100">
                                    {data.parameters.filter((p) => p.category === cat).map((p) => (
                                        <div key={p.param_id} className="px-4 py-3">
                                            <p className="text-sm text-slate-800">{p.parameter_name}</p>
                                            <p className="text-xs text-slate-400 mb-2">{p.measurement_criteria} · weight {p.weightage}</p>
                                            {p.score_type === 'auto' ? (
                                                <div className="flex gap-3">
                                                    <label className="text-xs text-slate-500">Target
                                                        <input type="number" className="block border border-slate-300 rounded-lg px-2 py-1 text-sm mt-1 w-28"
                                                            value={form[p.param_id]?.target_value ?? ''}
                                                            onChange={(e) => updateField(p.param_id, 'target_value', e.target.value)} />
                                                    </label>
                                                    <label className="text-xs text-slate-500">Achieved
                                                        <input type="number" className="block border border-slate-300 rounded-lg px-2 py-1 text-sm mt-1 w-28"
                                                            value={form[p.param_id]?.achieved_value ?? ''}
                                                            onChange={(e) => updateField(p.param_id, 'achieved_value', e.target.value)} />
                                                    </label>
                                                </div>
                                            ) : (
                                                <label className="text-xs text-slate-500">Achievement % (manager assessed)
                                                    <input type="number" className="block border border-slate-300 rounded-lg px-2 py-1 text-sm mt-1 w-28"
                                                        value={form[p.param_id]?.manual_score_pct ?? ''}
                                                        onChange={(e) => updateField(p.param_id, 'manual_score_pct', e.target.value)} />
                                                </label>
                                            )}
                                            <input placeholder="Remarks (optional)" className="mt-2 block w-full border border-slate-300 rounded-lg px-2 py-1 text-sm"
                                                value={form[p.param_id]?.remarks ?? ''} onChange={(e) => updateField(p.param_id, 'remarks', e.target.value)} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}

                    <button onClick={handleSubmit} className="bg-indigo-600 text-white text-sm rounded-lg px-5 py-2 hover:bg-indigo-700">
                        Save & Calculate
                    </button>
                </div>
            )}

            {result && (
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">
                        Result — Overall {result.overall_normalised}% ({result.overall_score.toFixed(1)} / {result.overall_max})
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        {Object.entries(result.categories).map(([cat, c]) => (
                            <div key={cat} className="border border-slate-200 rounded-lg p-3">
                                <p className="text-xs text-slate-500">{cat}</p>
                                <p className="text-lg font-semibold text-slate-800">{c.normalised}%</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Dashboard: Team leaderboard
// ---------------------------------------------------------------------------

function KRADashboard() {
    const [periodType, setPeriodType] = useState('monthly');
    const [periodValue, setPeriodValue] = useState(defaultMonthly);
    const [region, setRegion] = useState('');
    const [department, setDepartment] = useState('');
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    async function load() {
        setLoading(true);
        setError(null);
        try {
            const params = { action: 'team', period_type: periodType, period_value: periodValue };
            if (region) params.region = region;
            if (department) params.department = department;
            const res = await axios.get(`${SERVER_URL}/Rahul/report.php`, { params });
            setRows(res.data.data);
        } catch (e) {
            setError(extractError(e));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { load(); }, []);

    return (
        <div className="space-y-5">
            <div>
                <h1 className="text-2xl font-semibold text-slate-800">KRA Dashboard</h1>
                <p className="text-sm text-slate-500 mt-1">Team performance for the selected period, rolled up by category.</p>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-end gap-3">
                <label className="text-xs text-slate-500">Period type
                    <select className="block border border-slate-300 rounded-lg px-3 py-1.5 text-sm mt-1"
                        value={periodType} onChange={(e) => setPeriodType(e.target.value)}>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="annual">Annual</option>
                    </select>
                </label>
                <label className="text-xs text-slate-500">Period value
                    <input className="block border border-slate-300 rounded-lg px-3 py-1.5 text-sm mt-1 w-32"
                        value={periodValue} onChange={(e) => setPeriodValue(e.target.value)} />
                </label>
                <label className="text-xs text-slate-500">Region
                    <input className="block border border-slate-300 rounded-lg px-3 py-1.5 text-sm mt-1 w-32"
                        value={region} onChange={(e) => setRegion(e.target.value)} placeholder="optional" />
                </label>
                <label className="text-xs text-slate-500">Department
                    <input className="block border border-slate-300 rounded-lg px-3 py-1.5 text-sm mt-1 w-32"
                        value={department} onChange={(e) => setDepartment(e.target.value)} placeholder="optional" />
                </label>
                <button onClick={load} className="bg-slate-800 text-white text-sm rounded-lg px-4 py-1.5 hover:bg-slate-900">Refresh</button>
            </div>

            <ErrorBanner error={error} onDismiss={() => setError(null)} />
            {loading && <p className="text-sm text-slate-400">Loading…</p>}

            {!loading && rows.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-slate-50 text-left text-xs text-slate-500 uppercase tracking-wide">
                                <th className="px-4 py-2.5">#</th>
                                <th className="px-4 py-2.5">Employee</th>
                                <th className="px-4 py-2.5">Role</th>
                                <th className="px-4 py-2.5">Region</th>
                                <th className="px-4 py-2.5 text-right">Overall</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {rows.map((r, i) => (
                                <tr key={r.employee_id} className="hover:bg-slate-50">
                                    <td className="px-4 py-2.5 text-slate-400">{i + 1}</td>
                                    <td className="px-4 py-2.5 font-medium text-slate-800">{r.employee_name}</td>
                                    <td className="px-4 py-2.5 text-slate-500">{r.job_role}</td>
                                    <td className="px-4 py-2.5 text-slate-500">{r.region}</td>
                                    <td className={`px-4 py-2.5 text-right font-semibold ${scoreColor(r.overall_pct)}`}>{r.overall_pct}%</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {!loading && rows.length === 0 && !error && (
                <p className="text-sm text-slate-400">No scored employees found for this period/filter combination.</p>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// App shell — tab switcher between the three screens
// ---------------------------------------------------------------------------

const TABS = [
    { key: 'dashboard', label: 'Dashboard', component: KRADashboard },
    { key: 'entry', label: 'Score Entry', component: ScoreEntry },
    { key: 'admin', label: 'Template Builder', component: TemplateBuilder },
];

export default function KRASystem(onLogout) {
    const [tab, setTab] = useState('dashboard');
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const Active = TABS.find((t) => t.key === tab).component;

    return (
        <div className="min-h-screen bg-slate-50">
            <Sidebar
                onLogout={onLogout}
                isOpen={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
            />
            <div className="bg-white border-b border-slate-200">
                <div className="max-w-6xl mx-auto px-6 flex gap-1">

                    <button
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors border-0"
                    >
                        <Bars3Icon className="h-5 w-5" />
                    </button>
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            className={`px-4 py-3 text-sm font-medium border-b-2 transition ${tab === t.key ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500 hover:text-slate-800'
                                }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            </div>
            <div className="max-w-6xl mx-auto p-6">
                <Active />
            </div>
        </div>
    );
}